import React from "react";
import { Box, Text, Title, UnstyledButton } from "@mantine/core";
import styles from "./SettingsNav.module.css";

export interface SettingsNavItemModel {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  path: string;
}

export interface SettingsNavGroupModel {
  id: string;
  label: React.ReactNode;
  items: SettingsNavItemModel[];
}

export interface SettingsNavProps {
  /** Ordered, already visibility-filtered groups. */
  groups: SettingsNavGroupModel[];
  /** Currently active route path (compared against item.path). */
  activePath: string;
  /** Navigate handler (e.g. react-router's navigate). */
  onNavigate: (path: string) => void;
  /** Optional H1 rendered above the first group (e.g. "Settings"). */
  title?: React.ReactNode;
  /**
   * "sidebar" (default) renders a grouped vertical list with group headers
   * (desktop). "bar" renders a flat, header-less compact strip (mobile) —
   * same items and tap-to-navigate behavior, just laid out inline.
   */
  layout?: "sidebar" | "bar";
}

/**
 * The single, shared Settings navigation. Both the desktop sidebar and the
 * mobile bar render from the same grouped data through this component, so the
 * item markup and active-state styling live in exactly one place (replacing
 * the two hand-rolled, inline-styled renderers in SettingsPage). Styling is
 * token-driven (--settings-*).
 */
export default function SettingsNav({
  groups,
  activePath,
  onNavigate,
  title,
  layout = "sidebar",
}: SettingsNavProps) {
  const isBar = layout === "bar";
  return (
    <Box component="nav" aria-label="Settings" className={styles.root}>
      {title && (
        <Title order={1} fz="1.625rem" fw={700} className={styles.navTitle}>
          {title}
        </Title>
      )}
      <Box className={isBar ? styles.bar : styles.sidebar}>
        {groups.map((group) => (
          <Box key={group.id} className={styles.group}>
            {!isBar && (
              <Text component="p" className={styles.groupHeader}>
                {group.label}
              </Text>
            )}
            {group.items.map((item) => {
              const active = activePath === item.path;
              return (
                <UnstyledButton
                  key={item.id}
                  onClick={() => onNavigate(item.path)}
                  className={[styles.item, active && styles.itemActive]
                    .filter(Boolean)
                    .join(" ")}
                  aria-current={active ? "page" : undefined}
                >
                  {item.icon && (
                    <Box component="span" className={styles.icon}>
                      {item.icon}
                    </Box>
                  )}
                  <Text component="span" className={styles.label}>
                    {item.label}
                  </Text>
                </UnstyledButton>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
